/**
 * BulkActionBar — barra de acciones bulk para Publicaciones (extended mode).
 *
 * Visible cuando:
 *  - extendedMode === true (ExtendedModeContext, sólo superadmins).
 *  - selectedRowsCount >= 1.
 *
 * Position: sticky JUSTO arriba del header de la tabla (encima del row con
 * nombres de columnas). El z-index queda above scroll para mantener
 * accionables las opciones.
 *
 * Layout: barra horizontal con texto "N items seleccionados" a la izquierda,
 * 5 botones de acción a la derecha, y un botón "X" para limpiar.
 *
 * El componente orquesta la apertura de los modales internos y la integración
 * con los endpoints bulk en src/services/publicacionesBulk.ts. Recibe `clientId`,
 * `selectedIds`, callbacks de clear y refresh.
 */
import React, { useState, useCallback } from 'react';
import {
  EyeOff,
  HelpCircle,
  CheckCircle,
  Edit3,
  RefreshCw,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BulkConfirmModal, { BulkActionKind } from './BulkConfirmModal';
import BulkEditModal, { TaxonomyTree, BulkEditPayload } from './BulkEditModal';
import {
  bulkHide,
  bulkMarkDubious,
  bulkApprove,
  bulkEdit,
  bulkRecataloga,
  isBackendUnavailable,
  errorMessage,
} from '../../services/publicacionesBulk';

export interface BulkActionBarProps {
  /** Activa la barra (mode superadmin extended). */
  extendedMode: boolean;
  /** IDs seleccionados (id_catalogo). */
  selectedIds: string[];
  clientId: string | null | undefined;
  /** Callback para limpiar la selección desde el padre. */
  onClearSelection: () => void;
  /** Callback opcional para forzar refresh de la data tras una acción. */
  onAfterAction?: () => void;
  /** Taxonomía cliente para los dropdowns del modal Editar. */
  taxonomy?: TaxonomyTree | null;
}

type OpenModal = null | BulkActionKind | 'edit';

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  extendedMode,
  selectedIds,
  clientId,
  onClearSelection,
  onAfterAction,
  taxonomy = null,
}) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const count = selectedIds.length;
  const visible = extendedMode && count >= 1;

  const closeModal = useCallback(() => setOpenModal(null), []);

  const showToast = useCallback((kind: 'success' | 'error', msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const handleResult = useCallback(
    (label: string, processed?: number) => {
      const n = processed ?? count;
      showToast('success', `${label}: ${n.toLocaleString('es-AR')} items procesados.`);
      onClearSelection();
      onAfterAction?.();
    },
    [count, onAfterAction, onClearSelection, showToast]
  );

  const handleError = useCallback(
    (err: unknown) => {
      if (isBackendUnavailable(err)) {
        showToast('error', t('publicaciones.bulkActions.backendUnavailable'));
      } else {
        showToast('error', errorMessage(err));
      }
    },
    [showToast, t]
  );

  // --------------------------------------------------------------------------
  // Confirmar handlers
  // --------------------------------------------------------------------------

  const confirmHide = useCallback(
    async (comment: string) => {
      if (!clientId) throw new Error('Cliente no seleccionado');
      try {
        const res = await bulkHide(clientId, selectedIds, comment || undefined);
        handleResult(t('publicaciones.bulkActions.hide'), res.processed);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    [clientId, selectedIds, handleResult, handleError, t]
  );

  const confirmDubious = useCallback(
    async (comment: string) => {
      if (!clientId) throw new Error('Cliente no seleccionado');
      try {
        const res = await bulkMarkDubious(clientId, selectedIds, comment || undefined);
        handleResult(t('publicaciones.bulkActions.dubious'), res.processed);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    [clientId, selectedIds, handleResult, handleError, t]
  );

  const confirmApprove = useCallback(
    async (comment: string) => {
      if (!clientId) throw new Error('Cliente no seleccionado');
      try {
        const res = await bulkApprove(clientId, selectedIds, comment || undefined);
        handleResult(t('publicaciones.bulkActions.approve'), res.processed);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    [clientId, selectedIds, handleResult, handleError, t]
  );

  const confirmRecataloga = useCallback(
    async (comment: string) => {
      if (!clientId) throw new Error('Cliente no seleccionado');
      try {
        const res = await bulkRecataloga(clientId, selectedIds, comment || undefined);
        handleResult(t('publicaciones.bulkActions.recataloga'), res.processed);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    [clientId, selectedIds, handleResult, handleError, t]
  );

  const confirmEdit = useCallback(
    async (fields: BulkEditPayload, comment: string) => {
      if (!clientId) throw new Error('Cliente no seleccionado');
      try {
        const res = await bulkEdit(clientId, selectedIds, fields, comment || undefined);
        handleResult(t('publicaciones.bulkActions.edit'), res.processed);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    [clientId, selectedIds, handleResult, handleError, t]
  );

  if (!visible) return null;

  // Estilos de cada botón (paleta por acción).
  const btnBase =
    'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <>
      <div className="sticky top-0 z-20 bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 mb-2 flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 tabular-nums">
          {t('publicaciones.bulkActions.selectedCount', { count })}
        </span>
        {/* Hint discreto de keyboard nav. No traducido (microcopy interno
            superadmin). Se oculta en pantallas chicas para no romper layout. */}
        <span
          className="hidden md:inline text-[11px] text-gray-400 ml-2 select-none"
          title="↑/↓ navegar, Espacio toggle, Esc limpiar"
        >
          <kbd className="px-1 border border-gray-300 rounded bg-gray-50">↑</kbd>
          <kbd className="px-1 border border-gray-300 rounded bg-gray-50 ml-0.5">↓</kbd>
          <span className="mx-1">navegar</span>
          <kbd className="px-1 border border-gray-300 rounded bg-gray-50">Espacio</kbd>
          <span className="mx-1">toggle</span>
          <kbd className="px-1 border border-gray-300 rounded bg-gray-50">Esc</kbd>
          <span className="ml-1">limpiar</span>
        </span>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Ocultar */}
          <button
            type="button"
            onClick={() => setOpenModal('hide')}
            className={`${btnBase} bg-red-50 border-red-300 text-red-700 hover:bg-red-100`}
            title={t('publicaciones.bulkActions.hide')}
          >
            <EyeOff className="w-3.5 h-3.5" />
            {t('publicaciones.bulkActions.hide')}
          </button>

          {/* Dudoso */}
          <button
            type="button"
            onClick={() => setOpenModal('dubious')}
            className={`${btnBase} bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100`}
            title={t('publicaciones.bulkActions.dubious')}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            {t('publicaciones.bulkActions.dubious')}
          </button>

          {/* Aprobar */}
          <button
            type="button"
            onClick={() => setOpenModal('approve')}
            className={`${btnBase} bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100`}
            title={t('publicaciones.bulkActions.approve')}
          >
            <CheckCircle className="w-3.5 h-3.5" />
            {t('publicaciones.bulkActions.approve')}
          </button>

          {/* Editar */}
          <button
            type="button"
            onClick={() => setOpenModal('edit')}
            className={`${btnBase} bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100`}
            title={t('publicaciones.bulkActions.edit')}
          >
            <Edit3 className="w-3.5 h-3.5" />
            {t('publicaciones.bulkActions.edit')}
          </button>

          {/* Forzar re-cataloga */}
          <button
            type="button"
            onClick={() => setOpenModal('recataloga')}
            className={`${btnBase} bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100`}
            title={t('publicaciones.bulkActions.recataloga')}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t('publicaciones.bulkActions.recataloga')}
          </button>

          {/* Limpiar selección */}
          <button
            type="button"
            onClick={onClearSelection}
            className="p-1 text-gray-500 hover:text-gray-700 ml-1"
            title={t('publicaciones.bulkActions.clear')}
            aria-label="Limpiar selección"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Toast simple inline (no usamos lib externa) */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 max-w-sm px-3 py-2 text-sm rounded-md shadow-lg border ${
            toast.kind === 'success'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
              : 'bg-red-50 border-red-300 text-red-800'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Modales */}
      <BulkConfirmModal
        isOpen={openModal === 'hide'}
        action="hide"
        count={count}
        title={t('publicaciones.bulkActions.confirm.titleHide', {
          count: count.toLocaleString('es-AR'),
        })}
        description={t('publicaciones.bulkActions.confirm.descriptionGlobal')}
        confirmLabel={t('publicaciones.bulkActions.confirm.confirmHide')}
        cancelLabel={t('publicaciones.bulkActions.confirm.cancel')}
        commentPlaceholder={t('publicaciones.bulkActions.confirm.commentPlaceholder')}
        onClose={closeModal}
        onConfirm={confirmHide}
      />
      <BulkConfirmModal
        isOpen={openModal === 'dubious'}
        action="dubious"
        count={count}
        title={t('publicaciones.bulkActions.confirm.titleDubious', {
          count: count.toLocaleString('es-AR'),
        })}
        description={t('publicaciones.bulkActions.confirm.descriptionGlobal')}
        confirmLabel={t('publicaciones.bulkActions.confirm.confirmDubious')}
        cancelLabel={t('publicaciones.bulkActions.confirm.cancel')}
        commentPlaceholder={t('publicaciones.bulkActions.confirm.commentPlaceholder')}
        onClose={closeModal}
        onConfirm={confirmDubious}
      />
      <BulkConfirmModal
        isOpen={openModal === 'approve'}
        action="approve"
        count={count}
        title={t('publicaciones.bulkActions.confirm.titleApprove', {
          count: count.toLocaleString('es-AR'),
        })}
        description={t('publicaciones.bulkActions.confirm.descriptionGlobal')}
        confirmLabel={t('publicaciones.bulkActions.confirm.confirmApprove')}
        cancelLabel={t('publicaciones.bulkActions.confirm.cancel')}
        commentPlaceholder={t('publicaciones.bulkActions.confirm.commentPlaceholder')}
        onClose={closeModal}
        onConfirm={confirmApprove}
      />
      <BulkConfirmModal
        isOpen={openModal === 'recataloga'}
        action="recataloga"
        count={count}
        title={t('publicaciones.bulkActions.confirm.titleRecataloga', {
          count: count.toLocaleString('es-AR'),
        })}
        description={t('publicaciones.bulkActions.confirm.descriptionRecataloga')}
        confirmLabel={t('publicaciones.bulkActions.confirm.confirmRecataloga')}
        cancelLabel={t('publicaciones.bulkActions.confirm.cancel')}
        commentPlaceholder={t('publicaciones.bulkActions.confirm.commentPlaceholder')}
        onClose={closeModal}
        onConfirm={confirmRecataloga}
      />
      <BulkEditModal
        isOpen={openModal === 'edit'}
        count={count}
        taxonomy={taxonomy}
        onClose={closeModal}
        onConfirm={confirmEdit}
      />
    </>
  );
};

export default BulkActionBar;
